// A searchable branch filter for the commit graph, replacing the plain <select>.
// The trigger shows the active scope (a branch/ref name, or "All branches");
// opening it reveals a type-to-filter input over the repo's local + remote
// branches plus an "All branches" reset. The dropdown is portalled with
// position:fixed (like the workspace switcher) so the sticky graph toolbar and
// the column's overflow cannot clip it. Selection is reported via `onChange`
// (undefined clears the scope). Keyboard: type to filter, Up/Down to move,
// Enter to choose, Escape to close.

import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ReactElement,
} from 'react';
import { createPortal } from 'react-dom';
import { Check, ChevronDown, GitBranch } from 'lucide-react';
import type { GitBranches } from '@slate/core';

export interface GitBranchComboboxProps {
  /** The repo's branch listing, or null while loading. */
  branches: GitBranches | null;
  /** The current scope ref name, or undefined for "All branches". */
  value: string | undefined;
  /** Report a new scope; undefined clears it back to all branches. */
  onChange: (rev: string | undefined) => void;
}

/** One selectable row: `rev === undefined` is the "All branches" reset. */
interface Option {
  label: string;
  rev: string | undefined;
}

export function GitBranchCombobox({
  branches,
  value,
  onChange,
}: GitBranchComboboxProps): ReactElement {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const [pos, setPos] = useState<{ top: number; left: number; width: number }>({
    top: 0,
    left: 0,
    width: 0,
  });
  const btnRef = useRef<HTMLButtonElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const listId = useId();

  const options = useMemo<Option[]>(() => {
    const names = [
      ...(branches?.local ?? []).map((b) => b.name),
      ...(branches?.remote ?? []).map((b) => b.name),
    ];
    const q = query.trim().toLowerCase();
    const rows: Option[] = [];
    if (q === '' || 'all branches'.includes(q)) rows.push({ label: 'All branches', rev: undefined });
    for (const name of names) {
      if (q === '' || name.toLowerCase().includes(q)) rows.push({ label: name, rev: name });
    }
    return rows;
  }, [branches, query]);

  // Position the panel under the trigger and close on outside click / resize.
  useEffect(() => {
    if (!open) return;
    const place = (): void => {
      const b = btnRef.current?.getBoundingClientRect();
      if (b) setPos({ top: b.bottom + 4, left: b.left, width: Math.max(b.width, 220) });
    };
    place();
    const onDocDown = (e: MouseEvent): void => {
      if (
        !panelRef.current?.contains(e.target as Node) &&
        !btnRef.current?.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    window.addEventListener('resize', place);
    document.addEventListener('mousedown', onDocDown);
    return () => {
      window.removeEventListener('resize', place);
      document.removeEventListener('mousedown', onDocDown);
    };
  }, [open]);

  useEffect(() => {
    if (open) inputRef.current?.focus();
    else setQuery('');
  }, [open]);

  // Keep the active option in range as the filtered list shrinks/grows.
  useEffect(() => {
    setActiveIndex((i) => Math.min(i, Math.max(0, options.length - 1)));
  }, [options.length]);

  const choose = useCallback(
    (rev: string | undefined): void => {
      onChange(rev);
      setOpen(false);
      btnRef.current?.focus();
    },
    [onChange],
  );

  const onKeyDown = (e: React.KeyboardEvent): void => {
    if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
      btnRef.current?.focus();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => (options.length ? (i + 1) % options.length : 0));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => (options.length ? (i - 1 + options.length) % options.length : 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const opt = options[activeIndex];
      if (opt) choose(opt.rev);
    }
  };

  const triggerLabel = value ?? 'All branches';

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        className="mp-git-graph__filter-trigger"
        aria-label="Branch filter"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        onClick={() => setOpen((o) => !o)}
      >
        <GitBranch size={13} aria-hidden className="mp-git-graph__filter-icon" />
        <span className="mp-git-graph__filter-label">{triggerLabel}</span>
        <ChevronDown size={13} aria-hidden className="mp-git-graph__filter-chev" />
      </button>
      {open &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            ref={panelRef}
            className="mp-git-graph__filter-panel"
            style={{ position: 'fixed', top: pos.top, left: pos.left, width: pos.width }}
            onKeyDown={onKeyDown}
          >
            <input
              ref={inputRef}
              type="text"
              role="combobox"
              className="mp-git-graph__filter-search"
              aria-label="Filter branches"
              aria-expanded="true"
              aria-controls={listId}
              aria-autocomplete="list"
              placeholder="Filter branches…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            <ul id={listId} role="listbox" aria-label="Branches" className="mp-git-graph__filter-list">
              {options.map((opt, i) => {
                const selected = opt.rev === value;
                return (
                  <li key={opt.rev ?? ' all'}>
                    <button
                      type="button"
                      role="option"
                      aria-selected={selected}
                      data-active={i === activeIndex || undefined}
                      className="mp-git-graph__filter-option"
                      onMouseEnter={() => setActiveIndex(i)}
                      onClick={() => choose(opt.rev)}
                    >
                      <span className="mp-git-graph__filter-option-name">{opt.label}</span>
                      {selected && <Check size={13} aria-hidden />}
                    </button>
                  </li>
                );
              })}
              {options.length === 0 && (
                <li className="mp-git-graph__filter-empty">No matching branches</li>
              )}
            </ul>
          </div>,
          document.body,
        )}
    </>
  );
}
